// functions mapping one flight type to another that can be shared by bff and fe

import { format } from 'date-fns'
import { CabinClass, cabinCodeToCabinClassMap, FlightLegIdentifiers } from './common'
import {
  CabinName,
  cabinClassToCabinName,
  cabinNameToCabinClass,
  JourneyCabinSelection,
  JourneyResult,
} from './flight-availability'
import { FlightResultsQueryData, FlightSearchFormData } from './flight-form'
import { CabinClassSchema } from './flight-pricing'

const queryDateFormat = 'yyyy-MM-dd'

/**
 * Construct a journeyId,
 * to identify a group of outbound and inbound flights which will be booked together
 * */
export const generateJourneyId = (paramsArray: FlightLegIdentifiers[]): string => {
  return paramsArray
    ?.map(({ departureDate, flightNumber, marketingCarrier, rbdCode }) =>
      `${departureDate.replace(
        /Z.*/, // remove the timezone info
        ''
      )}${marketingCarrier}${flightNumber}${rbdCode}`.toUpperCase()
    )
    .join('_')
}

/**
 * Construct a journeyId supporting indirect flights,
 * to identify a group of outbound and inbound flights which will be booked together
 * */
export const generateIndirectJourneyId = (paramsArray: FlightLegIdentifiers[][]): string => {
  return paramsArray
    ?.map((legs) => {
      return legs
        ?.map(({ departureDate, flightNumber, marketingCarrier, rbdCode }) =>
          `${departureDate.replace(
            /Z.*/, // remove the timezone info
            ''
          )}${marketingCarrier}${flightNumber}${rbdCode}`.toUpperCase()
        )
        .join('_')
    })
    .join('_')
}

export const formDataToQueryParams = (data: FlightSearchFormData): FlightResultsQueryData => {
  const { dates, travellers } = data

  let returnDate: string | undefined

  if (!data.oneWayOnly && dates.endDateTime) {
    returnDate = format(dates.endDateTime, queryDateFormat)
  }

  return {
    departureDate: format(dates.startDateTime, queryDateFormat),
    returnDate,
    fromAirportCode: data.fromLocation.airportCode,
    toAirportCode: data.toLocation.airportCode,
    oneWayOnly: data.oneWayOnly,
    adult: travellers.adult.quantity.toString(),
    child: travellers.child.quantity.toString(),
    childrenAges: travellers.childrenAges.map((child) => child.age).join(','),
  }
}

export const availableCabinClasses = (journey: JourneyResult): CabinClass[] => {
  let availableCabinNames

  if (journey.direct) {
    availableCabinNames = Object.keys(journey.flights[0].cabinClasses) as CabinName[]
  } else {
    const firstFlight = journey.flights[0]
    const secondFlight = journey.flights[1]

    const firstFlightAvailableCabinNames = Object.keys(firstFlight.cabinClasses) as CabinName[]
    const secondFlightAvailableCabinNames = Object.keys(secondFlight.cabinClasses) as CabinName[]

    availableCabinNames = firstFlightAvailableCabinNames.filter((cabinClassName) =>
      secondFlightAvailableCabinNames.includes(cabinClassName)
    )
  }

  return availableCabinNames.map((cabinClassName) => cabinNameToCabinClass[cabinClassName])
}

export const getCabinInfo = (
  journey: JourneyResult,
  cabinClass: CabinClass
): Pick<JourneyCabinSelection, 'cabinCode' | 'rbd' | 'price'> => {
  const firstFlight = journey.flights[0]
  const cabinClassResult = Object.values(firstFlight.cabinClasses).find(
    (c) => cabinCodeToCabinClassMap[c.cabinCode] === cabinClass
  )

  if (!cabinClassResult) {
    throw new Error(`Couldn't find cabin class ${cabinClass} in flight result`)
  }

  const parsedCabinClass = CabinClassSchema.parse(cabinClass)
  const formattedCabinClass = cabinClassToCabinName[parsedCabinClass]

  let flightPrice = firstFlight.cabinClasses[formattedCabinClass]?.price
  if (!flightPrice) {
    throw new Error(`Couldn't find price for cabin class ${cabinClass} in first flight`)
  }
  if (!journey.direct) {
    const secondFlightPrice = journey.flights[1].cabinClasses[formattedCabinClass]?.price
    if (!secondFlightPrice) {
      throw new Error(`Couldn't find price for cabin class ${cabinClass} in second flight`)
    }
    flightPrice += secondFlightPrice
  }

  return {
    cabinCode: cabinClassResult.cabinCode,
    rbd: cabinClassResult.rbd,
    price: flightPrice,
  }
}
